<script setup lang="ts">
import type { SizesType } from '@ui/styles'
import IconButton from '@ui/components/icon/IconButton.vue'
import { ButtonVariant, Sizes } from '@ui/styles'
import Image from '~icons/cue-regular/image'
import SearchPlus from '~icons/cue-regular/search-plus'
import Icon from '../icon/Icon.vue'
import { PLACEHOLDER_IMG, placeholderImage } from './imageSettings'

const props = withDefaults(
  defineProps<{
    src?: string
    alt?: string
    zoom?: boolean
    showZoom?: boolean
    contain?: boolean
    lazy?: boolean
    placeholder?: string
    size?: SizesType
  }>(),
  {
    src: '',
    alt: 'Cue',
    zoom: false,
    showZoom: true,
    contain: false,
    lazy: true,
    placeholder: '',
  },
)

function getSrc() {
  return props.src || props.placeholder || placeholderImage.value
}

const url = ref(getSrc())
watch(
  () => props.src,
  () => {
    url.value = getSrc()
  },
)

function onError() {
  url.value = url.value === placeholderImage.value ? PLACEHOLDER_IMG : props.placeholder
}
</script>

<template lang="pug">
.image-container.br-16.flex-shrink-0(:class="[{ zoom, contain }, size]")
  img.image.s-100p(v-if="url" :src="url" :alt="alt" :loading="lazy ? 'lazy' : 'eager'" @error="onError")
  .empty-container.flex-center-center.s-100p(v-else)
    Icon.empty-image(:name="Image")
  IconButton.icon-zoom-in.m-8(v-if="zoom && showZoom" :name="SearchPlus" :size="Sizes.R2D2" :variant="ButtonVariant.Static")
</template>

<style scoped>
.image-container {
  overflow: hidden;
  position: relative;

  .image {
    object-fit: cover;
  }

  &.contain {
    .image {
      object-fit: contain;
    }
  }

  &.zoom {
    cursor: zoom-in;

    .image {
      transform-origin: center center;
      transition: 0.3s ease;
      transition-property: transform;

      &:hover {
        transform: scale(1.15);
      }
    }
  }

  &.bb8 {
    --uno: s-36 br-6;

    .empty-image {
      --uno: s-20;
    }
  }

  &.r2d2 {
    --uno: s-48 br-8;

    .empty-image {
      --uno: s-24;
    }
  }

  &.yoda {
    --uno: s-76 br-20;

    .empty-image {
      --uno: s-48;
    }
  }

  &.mandalorian {
    --uno: s-104 br-20;

    .empty-image {
      --uno: s-48;
    }
  }

  &.vader {
    --uno: s-122 br-20;

    .empty-image {
      --uno: s-48;
    }
  }
}

.empty-container {
  background: var(--cue-bg-neutral-primary);
  color: var(--cue-content-neutral-disabled);
}

.icon-zoom-in {
  position: absolute;
  top: 0;
  right: 0;
}
</style>
